import { TemplateState } from '@components/pdf/react-pdf/react-pdf.interface';
import { IBaseApiRes } from '@interfaces/generic.interface';
import { TFormTemplates } from '@interfaces/templates.interface';
import { api } from '@services/api';
import { handleError } from '@utils/api-error-handling';
import { encryptPayload } from '@utils/security';

export const templateApi = api
  .enhanceEndpoints({ addTagTypes: ['Templates', 'Template'] })
  .injectEndpoints({
    endpoints: (builder) => ({
      getTemplates: builder.query({
        query: ({
          category,
          templateName,
          department,
        }: {
          category?: string;
          templateName?: string;
          department?: string;
        }) => {
          return {
            url: `templates`,
            params: {
              ...(category && { 'filter[category]': category }),
              ...(templateName && { 'filter[templateName]': templateName }),
              ...(department && { 'filter[department]': department }),
            },
          };
        },
        providesTags: ['Templates'],
        transformErrorResponse: (baseQueryReturnValue) =>
          handleError(baseQueryReturnValue),
        transformResponse: (response: IBaseApiRes<TFormTemplates>) =>
          response.data,
        forceRefetch: () => true,
      }),
      getTemplate: builder.query({
        query: ({
          category,
          templateName,
          department,
          isDefault,
        }: {
          category?: string;
          templateName?: string;
          department?: string;
          isDefault?: boolean;
        }) => {
          return {
            url: `templates`,
            params: {
              ...(category && { 'filter[category]': category }),
              ...(templateName && { 'filter[templateName]': templateName }),
              ...(department && { 'filter[department]': department }),
              ...(isDefault !== undefined && {
                'filter[default]': String(isDefault),
              }),
            },
          };
        },
        providesTags: ['Template'],
        transformErrorResponse: (baseQueryReturnValue) =>
          handleError(baseQueryReturnValue),
        transformResponse: (response: IBaseApiRes<TFormTemplates>) =>
          response.data?.[0]?.content,
        forceRefetch: () => true,
      }),
      postTemplate: builder.mutation({
        query: (payload: {
          catalogTemplateID: string | number;
          isActive: boolean;
          signature: string;
          templateContent: TemplateState;
        }) => ({
          url: 'templates',
          method: 'POST',
          body: encryptPayload(payload),
        }),
        invalidatesTags: ['Templates'],
        transformResponse: (response: any) => response.data,
        transformErrorResponse: (baseQueryReturnValue) =>
          handleError(baseQueryReturnValue),
      }),
      postDefaultTemplate: builder.mutation({
        query: (payload: {
          department: string;
          category: string;
          templateName: string;
          templateContent: TemplateState;
          isMultiple?: boolean;
        }) => ({
          url: 'default-template',
          method: 'POST',
          body: encryptPayload(payload),
        }),
        invalidatesTags: ['Templates'],
        transformResponse: (response: any) => response.data,
        transformErrorResponse: (baseQueryReturnValue) =>
          handleError(baseQueryReturnValue),
      }),
      putTemplate: builder.mutation({
        query: ({
          templateID,
          payload,
        }: {
          templateID: string;
          payload: {
            templateContent: TemplateState;
            catalogTemplateID: string | number;
          };
        }) => ({
          url: `templates/${templateID}`,
          method: 'PUT',
          body: encryptPayload(payload),
        }),
        invalidatesTags: ['Templates'],
        transformResponse: (response: any) => response.data,
        transformErrorResponse: (baseQueryReturnValue) =>
          handleError(baseQueryReturnValue),
      }),
      putTemplateStatus: builder.mutation({
        query: ({
          templateID,
          payload,
        }: {
          templateID: string;
          payload: { catalogTemplateID: string | number; isActive: boolean };
        }) => ({
          url: `templates/${templateID}/status`,
          method: 'PUT',
          body: encryptPayload(payload),
        }),
        transformResponse: (response: any) => response.data,
        transformErrorResponse: (baseQueryReturnValue) =>
          handleError(baseQueryReturnValue),
      }),
      putDefaultTemplate: builder.mutation({
        query: ({
          catalogID,
          payload,
        }: {
          catalogID: string | number;
          payload: { defaultContent: TemplateState };
        }) => ({
          url: `default-templates/${catalogID}`,
          method: 'PUT',
          body: encryptPayload(payload),
        }),
        invalidatesTags: ['Templates'],
        transformResponse: (response: any) => response.data,
        transformErrorResponse: (baseQueryReturnValue) =>
          handleError(baseQueryReturnValue),
      }),
      deleteTemplate: builder.mutation({
        query: ({ catalogID }: { catalogID: string | number }) => ({
          url: `templates/${catalogID}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['Templates'],
        transformResponse: (response: any) => response.data,
        transformErrorResponse: (baseQueryReturnValue) =>
          handleError(baseQueryReturnValue),
      }),
    }),
  });

export const {
  useGetTemplatesQuery,
  useLazyGetTemplatesQuery,
  useGetTemplateQuery,
  useLazyGetTemplateQuery,
  usePostTemplateMutation,
  usePutTemplateMutation,
  usePutTemplateStatusMutation,
  usePutDefaultTemplateMutation,
  usePostDefaultTemplateMutation,
  useDeleteTemplateMutation,
} = templateApi;
